import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getScholarships } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { useConsultation } from "../components/Consultation";
import PageHero from "../components/layout/PageHero";
import SectionHeader from "../components/SectionHeader";
import Icon from "../components/Icon";
import { ScholarshipCard } from "../components/Cards";
import { CardSkeletons, ErrorState, EmptyState } from "./Home";
import { Reveal, Dots } from "../components/motion/Reveal";

const selectCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 transition-colors duration-200 focus:border-navy focus:outline-none";

const AID_TYPES = [
  { icon: "Award", title: "Scholarships", text: "Merit-based financial awards for academic excellence, leadership, sports, or talents." },
  { icon: "BadgePercent", title: "Discounts", text: "Tuition fee reductions such as early-bird offers, family rebates, or group enrolment savings." },
  { icon: "HandCoins", title: "Bursaries", text: "Need-based financial grants that support students from lower-income backgrounds." },
];

const WHY_OFFERED = [
  "To attract talented students who can enhance their academic reputation.",
  "To encourage diversity by welcoming students from different backgrounds and countries.",
  "To support access to education for deserving learners.",
  "To strengthen global partnerships and remain competitive in the international education sector.",
];

const TEAM_SUPPORT = [
  { icon: "SearchCheck", title: "Research & Updates", text: "We track scholarships, discounts, and bursaries from our partner institutions." },
  { icon: "UserCheck", title: "Matching Profiles", text: "Matching students with programs and institutions that align with their goals." },
  { icon: "FileText", title: "Application Support", text: "Guidance on essays, statements, and documentation." },
  { icon: "BellRing", title: "Deadline Reminders", text: "Ensuring no opportunity is missed." },
];

const STAY_INFORMED = [
  "Receive regular updates on available financial assistance.",
  "Get alerts on deadlines for scholarships and discounts.",
  "Access personalised counselling from our Admissions Team.",
  "Follow us via newsletters, webinars, and official website updates.",
];

const FEATURED_META = {
  "study-singapura-scholarship": {
    tagline: "Your gateway to Asia's innovation hub.",
    image: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?auto=format&fit=crop&w=1200&q=80",
  },
  "mauritius-study-scholarship": {
    tagline: "Experience quality education in a paradise island.",
    image: "https://images.unsplash.com/photo-1544550581-5f7ceaf7f992?auto=format&fit=crop&w=1200&q=80",
  },
};

export default function Scholarships() {
  useSEO(
    "Scholarships — Suvera Academy",
    "Scholarships, discounts and bursaries — Suvera Academy guides students to financial support offered by partner universities and colleges worldwide."
  );
  const openConsultation = useConsultation();
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [country, setCountry] = useState("");
  const [level, setLevel] = useState("");
  const [field, setField] = useState("");
  const [type, setType] = useState("");

  useEffect(() => {
    getScholarships()
      .then(setScholarships)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const options = (key) => [...new Set(scholarships.map((s) => s[key]))].sort();
  const featured = scholarships.filter((s) => FEATURED_META[s.id]);

  const filtered = useMemo(
    () =>
      scholarships.filter((s) => {
        if (country && s.country !== country) return false;
        if (level && s.studyLevel !== level) return false;
        if (field && s.field !== field) return false;
        if (type && s.type !== type) return false;
        return true;
      }),
    [scholarships, country, level, field, type]
  );

  return (
    <main data-testid="scholarships-page">
      <PageHero
        eyebrow="Scholarships"
        title="Making Education Affordable and Accessible"
        description="At Suvera Academy, we believe financial barriers should never stop a student from achieving their dreams. That's why we guide students through scholarships, discounts, and bursaries offered by our partner universities and colleges around the world."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Scholarships" }]}
        testid="scholarships-hero"
      />

      {/* What are they */}
      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="aid-types-section">
        <SectionHeader
          chapter="Chapter 01"
          eyebrow="What Are They?"
          title="Scholarships, Discounts & Bursaries"
          description="Together, these options reduce the cost of studying abroad and make education more accessible."
        />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {AID_TYPES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08} className="h-full">
              <div className="group h-full rounded-2xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]" data-testid={`aid-type-${a.title.toLowerCase()}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-navy group-hover:text-white">
                  <Icon name={a.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-2xl font-semibold text-navy-dark">{a.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why offered */}
      <section className="bg-sand">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="why-offered-section">
          <SectionHeader
            chapter="Chapter 02"
            eyebrow="The Bigger Picture"
            title="Why Do Universities and Colleges Offer Them?"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {WHY_OFFERED.map((r, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div className="flex h-full items-start gap-5 rounded-2xl border border-slate-200 bg-white p-6 sm:p-7" data-testid={`why-offered-${i}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy font-mono-accent text-xs font-bold text-white">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-sm leading-relaxed text-slate-700 sm:text-base">{r}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team support */}
      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="team-support-section">
        <SectionHeader
          chapter="Chapter 03"
          eyebrow="How We Help"
          title="How the Suvera Academy Admissions Team Supports You"
          description="Our Admissions Department works tirelessly to connect students with the best financial support opportunities."
        />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TEAM_SUPPORT.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.08} className="h-full">
              <div className="group h-full rounded-2xl border border-slate-200 bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]" data-testid={`team-support-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-navy group-hover:text-white">
                  <Icon name={t.icon} className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-navy-dark">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Stay informed */}
      <section className="bg-sand">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="stay-informed-section">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionHeader
                chapter="Chapter 04"
                eyebrow="Stay Updated"
                title="How Students Stay Informed"
                description="By staying connected with Suvera Academy, students can:"
              />
              <Reveal>
                <ul className="space-y-3.5">
                  {STAY_INFORMED.map((p) => (
                    <li key={p} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-700 sm:text-base">
                      <Icon name="CheckCircle2" className="mt-0.5 h-5 w-5 shrink-0 text-dot-green" />
                      {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.15}>
                <div className="rounded-2xl bg-navy p-8 text-white sm:p-12" data-testid="stay-informed-callout">
                  <Dots className="mb-6" />
                  <p className="font-display text-2xl font-semibold leading-snug sm:text-3xl">
                    You don't have to search on your own — Suvera Academy brings the right opportunities directly to you.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="hero-spotlight" data-testid="commitment-section">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <Reveal className="max-w-4xl">
            <p className="mb-4 flex items-center gap-3 font-mono-accent text-xs uppercase tracking-[0.25em] text-dot-amber">
              <Dots size="h-1.5 w-1.5" /> Our Commitment
            </p>
            <p className="font-display text-2xl font-semibold leading-snug text-white sm:text-3xl lg:text-4xl" data-testid="commitment-statement">
              At Suvera Academy, financial assistance is more than just reducing costs. It's about removing barriers, empowering talent, and unlocking potential — so every deserving student can study abroad with confidence.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Featured scholarships */}
      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="featured-scholarships-section">
        <SectionHeader chapter="Chapter 05" eyebrow="Featured" title="Our Featured Scholarships" />
        {loading ? (
          <CardSkeletons count={2} />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {featured.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1}>
                <div className="group relative block min-h-[340px] overflow-hidden rounded-2xl bg-navy-dark" data-testid={`featured-scholarship-${s.id}`}>
                  <img
                    src={FEATURED_META[s.id].image}
                    alt={s.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover opacity-75 transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/35 to-transparent" />
                  <div className="relative flex h-full flex-col justify-end p-7 sm:p-9">
                    <span className="mb-3 inline-flex w-max items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-white/80 backdrop-blur-sm">
                      <Icon name="MapPin" className="h-3 w-3" /> {s.country}
                    </span>
                    <h3 className="font-display text-2xl font-semibold text-white sm:text-3xl">{s.name}</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-white/75">{FEATURED_META[s.id].tagline}</p>
                    <button
                      onClick={openConsultation}
                      className="mt-5 inline-flex w-max items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-dot-amber hover:text-navy-dark"
                      data-testid={`featured-scholarship-enquire-${s.id}`}
                    >
                      Check Eligibility
                      <Icon name="ArrowRight" className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Directory */}
      <section className="bg-sand">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="scholarship-directory">
          <SectionHeader
            chapter="Chapter 06"
            eyebrow="Directory"
            title="Browse Current Opportunities"
            description="Filter by destination, study level, field and type. An advisor can confirm current availability and your eligibility."
          />
          <div className="mb-10 grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="scholarship-filters">
            <select className={selectCls} value={country} onChange={(e) => setCountry(e.target.value)} data-testid="scholarship-country-filter" aria-label="Filter by country">
              <option value="">All Countries</option>
              {options("country").map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className={selectCls} value={level} onChange={(e) => setLevel(e.target.value)} data-testid="scholarship-level-filter" aria-label="Filter by study level">
              <option value="">All Study Levels</option>
              {options("studyLevel").map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select className={selectCls} value={field} onChange={(e) => setField(e.target.value)} data-testid="scholarship-field-filter" aria-label="Filter by field">
              <option value="">All Fields</option>
              {options("field").map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <select className={selectCls} value={type} onChange={(e) => setType(e.target.value)} data-testid="scholarship-type-filter" aria-label="Filter by scholarship type">
              <option value="">All Types</option>
              {options("type").map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {loading ? (
            <CardSkeletons count={6} />
          ) : error ? (
            <ErrorState message="Scholarships could not be loaded. Please refresh the page." />
          ) : filtered.length === 0 ? (
            <EmptyState message="No scholarships found for this filter. Try adjusting your selection." />
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="scholarships-grid">
              {filtered.map((s, i) => (
                <Reveal key={s.id} delay={(i % 3) * 0.06} className="h-full">
                  <div className="flex h-full flex-col">
                    <div className="flex-1"><ScholarshipCard s={s} /></div>
                    <button
                      onClick={openConsultation}
                      className="mt-3 inline-flex items-center gap-2 self-start text-sm font-semibold text-navy transition-colors duration-200 hover:text-dot-blue"
                      data-testid={`scholarship-enquire-${s.id}`}
                    >
                      Check Eligibility <Icon name="ArrowRight" className="h-4 w-4" />
                    </button>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
          <p className="mt-10 max-w-2xl text-xs leading-relaxed text-slate-400" data-testid="scholarship-placeholder-note">
            Scholarship coverage and deadlines shown are indicative. Suvera Academy does not guarantee scholarship outcomes — an advisor will confirm current opportunities, amounts and deadlines for your profile.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="hero-spotlight" data-testid="scholarships-final-cta">
        <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <Reveal className="max-w-3xl">
            <Dots className="mb-5" />
            <h2 className="font-display text-3xl font-semibold leading-[1.12] tracking-tight text-white sm:text-4xl lg:text-5xl">
              Exclusive Student Discounts — Learn More, Pay Less!
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              Talk to our admissions team about the scholarships, discounts and bursaries available for your profile.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-dot-amber hover:text-navy-dark"
                data-testid="scholarships-contact-button"
              >
                Contact Us
                <Icon name="ArrowRight" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <button
                onClick={openConsultation}
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-white/60 hover:bg-white/5"
                data-testid="scholarships-consultation-button"
              >
                Book a Free Consultation
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
